'use client'

import * as React from 'react'
import { useFormContext, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { MultiSelect } from '@/components/ui/multi-select'

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { CurrencyPriceInput, type CurrencyOptionsType } from '@/components/form/currency-price-input'
import { FormImageUpload } from '@/components/form/form-image-upload'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { useSendCloudIntegration } from '@/lib/api/integrations'
import { useStoreCurrencies } from '@/lib/api/stores'
import { createZeroCurrencyOptions } from '@/lib/utils/currency'
import type { CreateShippingRateFormData } from './validation'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { CountryMultiSelect } from '@/components/country-multi-select'
import { countries } from '@/data/countries'

type ShippingRateFormData = CreateShippingRateFormData

/**
 * Shipping rate form component
 * Handles shipping rate fields using React Hook Form with Controller
 * Works for both create and update forms
 */
export const ShippingRateForm = () => {
	const { t } = useTranslation(['shippings', 'common'])
	const { id: storeId } = useStoreNavigate()
	const {
		control,
		formState: { errors },
		setValue,
	} = useFormContext<ShippingRateFormData>()

	const isFree = useWatch({ control, name: 'isFree' })
	const isPickupPoint = useWatch({ control, name: 'isPickupPoint' })
	const countryCodesValue = useWatch({ control, name: 'countryCodes' })

	const isCountryCodesEmpty = !countryCodesValue || countryCodesValue.length === 0
	const [allCountry, setAllCountry] = React.useState(true)

	React.useEffect(() => {
		setAllCountry(isCountryCodesEmpty)
	}, [])

	const handleAllCountryChange = (checked: boolean) => {
		setAllCountry(checked)
		if (checked && !isCountryCodesEmpty) {
			setValue('countryCodes', [])
		}
	}
	const currencies = useStoreCurrencies(storeId)
	const { data: sendCloudIntegration } = useSendCloudIntegration(storeId, true)

	const sendCloudCarriers = (sendCloudIntegration?.credentials as { carriers?: { value: string; name: string }[] })?.carriers || []

	return (
		<div className="space-y-6">
			{/* Basic Information */}
			<Card>
				<CardHeader>
					<CardTitle>{t('form.information.title')}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<Controller
						name="shippingRateName"
						control={control}
						render={({ field }) => (
							<div className="space-y-2">
								<Label htmlFor="shippingRateName">
									{t('form.information.name.label')} <span className="text-red-500">*</span>
								</Label>
								<Input
									id="shippingRateName"
									{...field}
									value={field.value || ''}
									placeholder={t('form.information.name.placeholder')}
								/>
								{errors.shippingRateName && (
									<p className="mt-1 text-xs leading-tight text-red-600">
										{errors.shippingRateName.message as string}
									</p>
								)}
							</div>
						)}
					/>

					<Controller
						name="description"
						control={control}
						render={({ field }) => (
							<div className="space-y-2">
								<Label htmlFor="description">{t('form.information.description.label')}</Label>
								<Textarea
									id="description"
									{...field}
									value={field.value || ''}
									placeholder={t('form.information.description.placeholder')}
									rows={3}
								/>
								{errors.description && (
									<p className="mt-1 text-xs leading-tight text-red-600">
										{errors.description.message as string}
									</p>
								)}
							</div>
						)}
					/>

					<Controller
						name="icon"
						control={control}
						render={({ field }) => (
							<div className="space-y-2">
								<Label htmlFor="icon">{t('form.information.icon.label')}</Label>
								<FormImageUpload
									id="icon"
									name="icon"
									url={field.value || null}
									onImageChange={(url) => field.onChange(url || undefined)}
								>
									<p className="mt-2 text-xs text-muted-foreground">
										{t('form.information.icon.hint')}
									</p>
								</FormImageUpload>
								{errors.icon && (
									<p className="mt-1 text-xs leading-tight text-red-600">
										{errors.icon.message as string}
									</p>
								)}
							</div>
						)}
					/>
				</CardContent>
			</Card>

			{/* Pricing */}
			<Card>
				<CardHeader>
					<CardTitle>{t('form.pricing.title')}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<Controller
						name="isFree"
						control={control}
						render={({ field }) => (
							<div className="flex items-center space-x-2">
								<Switch
									id="isFree"
									checked={field.value || false}
									onCheckedChange={(checked) => {
										field.onChange(checked)
										if (checked && currencies.data) {
											setValue('amount', createZeroCurrencyOptions(currencies.data))
										}
									}}
								/>
								<Label htmlFor="isFree">{t('form.pricing.isFree.label')}</Label>
							</div>
						)}
					/>

					<div
						style={{
							opacity: isFree ? 0.3 : 1,
							pointerEvents: isFree ? 'none' : 'all',
						}}
					>
						<Controller
							name="amount"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="amount">
										{t('form.pricing.amount.label')} <span className="text-red-500">*</span>
									</Label>
									<CurrencyPriceInput
										values={field.value ?? null}
										onChange={(data) => field.onChange(data.currencyOptions)}
										storeId={storeId}
									/>
									{errors.amount && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.amount.message as string}
										</p>
									)}
								</div>
							)}
						/>
					</div>
				</CardContent>
			</Card>

			{/* Cart Conditions */}
			<Card>
				<CardHeader>
					<CardTitle>{t('form.conditions.title')}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						<Controller
							name="shippingRateMinAmount"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="shippingRateMinAmount">{t('form.conditions.minAmount.label')}</Label>
									<CurrencyPriceInput
										values={field.value ?? null}
										onChange={(data) => field.onChange(data.currencyOptions)}
										storeId={storeId}
									/>
									{errors.shippingRateMinAmount && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.shippingRateMinAmount.message as string}
										</p>
									)}
								</div>
							)}
						/>

						<Controller
							name="shippingRateMaxAmount"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="shippingRateMaxAmount">{t('form.conditions.maxAmount.label')}</Label>
									<CurrencyPriceInput
										values={field.value ?? null}
										onChange={(data) => field.onChange(data.currencyOptions)}
										storeId={storeId}
									/>
									{errors.shippingRateMaxAmount && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.shippingRateMaxAmount.message as string}
										</p>
									)}
								</div>
							)}
						/>
					</div>

					<div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
						<Controller
							name="shippingRateMinWeight"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="shippingRateMinWeight">{t('form.conditions.minWeight.label')}</Label>
									<Input
										id="shippingRateMinWeight"
										type="number"
										step="1"
										{...field}
										value={field.value || ''}
										onChange={(e) => {
											const value = e.target.value
											field.onChange(value === '' ? null : parseFloat(value) || null)
										}}
										placeholder={t('form.conditions.minWeight.placeholder')}
									/>
									{errors.shippingRateMinWeight && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.shippingRateMinWeight.message as string}
										</p>
									)}
								</div>
							)}
						/>

						<Controller
							name="shippingRateMaxWeight"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="shippingRateMaxWeight">{t('form.conditions.maxWeight.label')}</Label>
									<Input
										id="shippingRateMaxWeight"
										type="number"
										step="1"
										{...field}
										value={field.value || ''}
										onChange={(e) => {
											const value = e.target.value
											field.onChange(value === '' ? null : parseFloat(value) || null)
										}}
										placeholder={t('form.conditions.maxWeight.placeholder')}
									/>
									{errors.shippingRateMaxWeight && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.shippingRateMaxWeight.message as string}
										</p>
									)}
								</div>
							)}
						/>
					</div>
				</CardContent>
			</Card>

			{/* Additional Options */}
			<Card>
				<CardHeader>
					<CardTitle>{t('form.options.title')}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<Controller
						name="highlighted"
						control={control}
						render={({ field }) => (
							<div className="flex items-center space-x-2">
								<Switch
									id="highlighted"
									checked={field.value || false}
									onCheckedChange={field.onChange}
								/>
								<Label htmlFor="highlighted">{t('form.options.highlighted.label')}</Label>
							</div>
						)}
					/>

					<Controller
						name="isPickupPoint"
						control={control}
						render={({ field }) => (
							<div className="flex items-center space-x-2">
								<Switch
									id="isPickupPoint"
									checked={field.value || false}
									onCheckedChange={field.onChange}
									disabled={!sendCloudIntegration?.enabled}
								/>
								<Label htmlFor="isPickupPoint">{t('form.options.isPickupPoint.label')}</Label>
							</div>
						)}
					/>

					{sendCloudIntegration?.enabled && isPickupPoint ? (
						<Controller
							name="pickupPointCarriers"
							control={control}
							render={({ field }) => (
								<div className="space-y-2">
									<Label htmlFor="pickupPointCarriers">
										{t('form.options.pickupPointCarriers.label')}
									</Label>
									<MultiSelect
										options={sendCloudCarriers.map((carrier) => ({
											label: carrier.name,
											value: carrier.value,
										}))}
										selected={field.value || []}
										onChange={(values) => field.onChange(values)}
										placeholder={t('form.options.pickupPointCarriers.placeholder')}
									/>
									{errors.pickupPointCarriers && (
										<p className="mt-1 text-xs leading-tight text-red-600">
											{errors.pickupPointCarriers.message as string}
										</p>
									)}
								</div>
							)}
						/>
					) : (
						!sendCloudIntegration?.enabled && (
							<Alert variant="warning">
								<AlertTitle>{t('form.options.sendCloudWarning.title')}</AlertTitle>
								<AlertDescription>
									{t('form.options.sendCloudWarning.description')}
								</AlertDescription>
							</Alert>
						)
					)}

					<div className="space-y-4">
						<div className="flex items-center space-x-2">
							<Switch
								id="allCountry"
								checked={allCountry}
								onCheckedChange={handleAllCountryChange}
							/>
							<Label htmlFor="allCountry">{t('form.options.allCountry.label')}</Label>
						</div>

						{!allCountry && (
							<Controller
								name="countryCodes"
								control={control}
								render={({ field }) => (
									<div className="space-y-2">
										<Label htmlFor="countryCodes">{t('form.options.countries.label')}</Label>
										<p className="text-xs text-muted-foreground">
											{t('form.options.countries.description')}
										</p>
										<CountryMultiSelect
											valueKey="country-code"
											countryCodes={countries.map((country) => country['alpha-2'])}
											selected={field.value ?? []}
											onChange={field.onChange}
										/>
										{errors.countryCodes && (
											<p className="mt-1 text-xs leading-tight text-red-600">
												{errors.countryCodes.message as string}
											</p>
										)}
									</div>
								)}
							/>
						)}
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
